<template>
  <div class="ou-settings">
    <header class="ou-settings__hero">
      <div>
        <p class="ou-settings__eyebrow">Store configuration</p>
        <h2>Operational units</h2>
        <p>
          Define the store's own terminology and organize the concrete places where work and sales
          happen. Existing workflows remain optional and backward compatible.
        </p>
      </div>
      <div class="ou-settings__actions">
        <hs-button variant="secondary" icon="tag" @click="openTypeForm()">New type</hs-button>
        <hs-button variant="primary" icon="plus" :disabled="!types.length" @click="openUnitForm()">
          New unit
        </hs-button>
      </div>
    </header>

    <hs-alert v-if="error" tone="danger" :message="error" />

    <section class="ou-stat-grid">
      <article v-for="metric in summaryMetrics" :key="metric.label">
        <span>{{ metric.label }}</span>
        <strong>{{ metric.value }}</strong>
      </article>
    </section>

    <section class="ou-settings__section">
      <div class="ou-section-title">
        <div>
          <h3>Store-defined types</h3>
          <p>Labels shown throughout ticket and sale workflows—never fixed business terminology.</p>
        </div>
      </div>
      <div v-if="loading" class="ou-empty">Loading operational units…</div>
      <div v-else-if="!types.length" class="ou-empty">
        <strong>No unit types yet</strong>
        <span>Create a type such as Room, Lane, Chair, or any term this store uses.</span>
      </div>
      <div v-else class="ou-types">
        <article
          v-for="type in orderedTypes"
          :key="type.id"
          class="ou-type"
          :class="{ 'ou-type--archived': type.active === false }"
        >
          <span class="ou-type__swatch" :style="{ background: type.color || '#3293ec' }">
            <hs-icon :name="type.icon || 'map-pin'" :size="16" />
          </span>
          <div>
            <strong>{{ type.plural_label }}</strong>
            <small>{{ type.singular_label }} · {{ type.code }}</small>
          </div>
          <hs-badge :tone="type.active === false ? 'neutral' : 'success'">
            {{ type.active === false ? "Archived" : "Active" }}
          </hs-badge>
          <div class="ou-row-actions">
            <button type="button" @click="openTypeForm(type)">Edit</button>
            <button type="button" @click="toggleTypeArchive(type)">
              {{ type.active === false ? "Restore" : "Archive" }}
            </button>
          </div>
        </article>
      </div>
    </section>

    <section class="ou-settings__section">
      <div class="ou-section-title">
        <div>
          <h3>Units & hierarchy</h3>
          <p>Availability is derived from active tickets and direct sales, not manually persisted.</p>
        </div>
        <hs-button variant="secondary" icon="refresh-cw" size="sm" @click="load">Refresh</hs-button>
      </div>

      <div class="ou-filters">
        <hs-select v-model="filters.typeId" label="Type" :options="typeFilterOptions" />
        <hs-select v-model="filters.availability" label="Availability" :options="availabilityOptions" />
        <hs-select v-model="filters.active" label="Lifecycle" :options="lifecycleOptions" />
      </div>

      <div v-if="!filteredUnits.length" class="ou-empty">
        <strong>No matching units</strong>
        <span>Create a unit or change the current filters.</span>
      </div>
      <div v-else class="ou-unit-list">
        <article
          v-for="entry in hierarchicalUnits"
          :key="entry.unit.id"
          class="ou-unit"
          :class="{ 'ou-unit--archived': entry.unit.active === false }"
          :style="{ '--depth': entry.depth }"
        >
          <div class="ou-unit__identity">
            <span class="ou-unit__branch" aria-hidden="true">↳</span>
            <span
              class="ou-unit__icon"
              :style="{ background: `${typeFor(entry.unit)?.color || '#3293ec'}20`, color: typeFor(entry.unit)?.color || '#3293ec' }"
            >
              <hs-icon :name="typeFor(entry.unit)?.icon || 'map-pin'" :size="16" />
            </span>
            <div>
              <strong>{{ entry.unit.name }}</strong>
              <small>{{ typeFor(entry.unit)?.singular_label || "Unit" }} · {{ entry.unit.code }}</small>
            </div>
          </div>
          <div class="ou-unit__badges">
            <hs-badge :tone="availabilityTone(availabilityFor(entry.unit))">
              {{ availabilityFor(entry.unit) }}
            </hs-badge>
            <hs-badge v-if="entry.unit.out_of_service" tone="danger">Out of service</hs-badge>
            <hs-badge v-if="entry.unit.active === false" tone="neutral">Archived</hs-badge>
            <span v-if="entry.unit.capacity">Capacity {{ entry.unit.capacity }}</span>
          </div>
          <div class="ou-row-actions">
            <button type="button" @click="openUnitForm(entry.unit)">Edit</button>
            <button
              v-if="availabilityFor(entry.unit) === 'occupied'"
              type="button"
              @click="openReleaseDialog(entry.unit)"
            >
              Release
            </button>
            <button type="button" @click="toggleOutOfService(entry.unit)">
              {{ entry.unit.out_of_service ? "Return to service" : "Take out of service" }}
            </button>
            <button type="button" @click="toggleUnitArchive(entry.unit)">
              {{ entry.unit.active === false ? "Restore" : "Archive" }}
            </button>
          </div>
        </article>
      </div>
    </section>

    <hs-modal
      v-model="showTypeForm"
      :title="editingType ? `Edit ${editingType.singular_label}` : 'Create operational unit type'"
      description="Choose the language users will see throughout the store."
      size="lg"
    >
      <form id="operational-unit-type-form" class="ou-form" @submit.prevent="saveType">
        <hs-input v-model="typeForm.code" label="Code" placeholder="ROOM" required />
        <hs-input v-model="typeForm.singular_label" label="Singular label" placeholder="Room" required />
        <hs-input v-model="typeForm.plural_label" label="Plural label" placeholder="Rooms" required />
        <hs-input v-model="typeForm.icon" label="Icon" placeholder="home" />
        <label class="ou-native-field">
          <span>Color</span>
          <input v-model="typeForm.color" type="color" />
        </label>
        <hs-input v-model.number="typeForm.sort_order" label="Sort order" type="number" />
        <div class="ou-form__wide">
          <hs-textarea v-model="typeForm.description" label="Description" placeholder="Optional guidance for staff" />
        </div>
        <hs-toggle v-model="typeForm.active" label="Active" />
      </form>
      <template #footer>
        <hs-button variant="secondary" @click="showTypeForm = false">Cancel</hs-button>
        <hs-button
          form="operational-unit-type-form"
          variant="primary"
          :loading="saving"
          :disabled="saving"
          @click="saveType"
        >
          Save type
        </hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showUnitForm"
      :title="editingUnit ? `Edit ${editingUnit.name}` : 'Create operational unit'"
      description="Units can be nested under other units to represent floors, sections, wings, or halls."
      size="lg"
    >
      <form id="operational-unit-form" class="ou-form" @submit.prevent="saveUnit">
        <hs-select
          v-model="unitForm.operational_unit_type_id"
          label="Type"
          :options="activeTypeOptions"
          required
        />
        <hs-input v-model="unitForm.code" label="Code" placeholder="ROOM-101" required />
        <hs-input v-model="unitForm.name" label="Name" placeholder="Room 101" required />
        <hs-select v-model="unitForm.parent_id" label="Parent" :options="parentOptions" />
        <hs-input v-model="unitForm.revenue_center_id" label="Revenue center ID" placeholder="Optional" />
        <hs-input v-model.number="unitForm.capacity" label="Capacity" type="number" />
        <hs-input v-model.number="unitForm.sort_order" label="Sort order" type="number" />
        <div class="ou-form__wide">
          <hs-textarea v-model="unitForm.description" label="Description" placeholder="Optional staff-facing details" />
        </div>
        <div class="ou-form__wide">
          <hs-textarea
            v-model="unitForm.attributes"
            label="Attributes (JSON)"
            placeholder='{"floor": 1, "window": true}'
            :error="attributesError"
          />
        </div>
        <hs-toggle v-model="unitForm.exclusive_use" label="Exclusive use" />
        <hs-toggle v-model="unitForm.out_of_service" label="Out of service" />
        <hs-toggle v-model="unitForm.active" label="Active" />
      </form>
      <template #footer>
        <hs-button variant="secondary" @click="showUnitForm = false">Cancel</hs-button>
        <hs-button
          form="operational-unit-form"
          variant="primary"
          :loading="saving"
          :disabled="saving"
          @click="saveUnit"
        >
          Save unit
        </hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showReleaseDialog"
      title="Release operational unit"
      description="Unassign all active assignments from this unit. Provide a reason for the audit trail."
      size="md"
    >
      <form id="release-unit-form" class="ou-form" @submit.prevent="releaseUnit">
        <p v-if="releaseTarget" class="ou-form__info">
          Releasing: <strong>{{ releaseTarget.name }}</strong>
        </p>
        <hs-textarea
          v-model="releaseReason"
          label="Reason"
          placeholder="e.g., Abandoned transaction, System error recovery"
          required
        />
      </form>
      <template #footer>
        <hs-button variant="secondary" @click="showReleaseDialog = false">Cancel</hs-button>
        <hs-button
          form="release-unit-form"
          variant="primary"
          :loading="releasingUnit"
          :disabled="releasingUnit || !releaseReason.trim()"
          @click="releaseUnit"
        >
          Release unit
        </hs-button>
      </template>
    </hs-modal>
  </div>
</template>

<script>
import {
  HsAlert,
  HsBadge,
  HsButton,
  HsIcon,
  HsInput,
  HsModal,
  HsSelect,
  HsTextarea,
  HsToggle,
} from "@/components/ui";
import {
  createOperationalUnit,
  createOperationalUnitType,
  getOperationalUnitAvailability,
  getOperationalUnitStatistics,
  getOperationalUnits,
  getOperationalUnitTypes,
  operationalUnitError,
  releaseOperationalUnit,
  updateOperationalUnit,
  updateOperationalUnitType,
} from "@/services/operationalUnitsService";

const blankType = () => ({
  code: "",
  singular_label: "",
  plural_label: "",
  description: "",
  icon: "map-pin",
  color: "#3293ec",
  active: true,
  sort_order: 0,
});

const blankUnit = () => ({
  operational_unit_type_id: "",
  code: "",
  name: "",
  description: "",
  parent_id: "",
  revenue_center_id: "",
  capacity: "",
  attributes: "{}",
  exclusive_use: true,
  out_of_service: false,
  active: true,
  sort_order: 0,
});

export default {
  name: "OperationalUnitsSettings",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsIcon,
    HsInput,
    HsModal,
    HsSelect,
    HsTextarea,
    HsToggle,
  },
  props: {
    storeId: { type: [String, Number], required: true },
  },
  data() {
    return {
      types: [],
      units: [],
      availability: [],
      statistics: {},
      loading: false,
      saving: false,
      error: "",
      showTypeForm: false,
      showUnitForm: false,
      editingType: null,
      editingUnit: null,
      typeForm: blankType(),
      unitForm: blankUnit(),
      attributesError: "",
      filters: { typeId: "", availability: "", active: "true" },
      availabilityOptions: [
        { value: "", label: "All availability" },
        { value: "available", label: "Available" },
        { value: "occupied", label: "Occupied" },
        { value: "reserved", label: "Reserved" },
        { value: "unavailable", label: "Unavailable" },
      ],
      lifecycleOptions: [
        { value: "", label: "Active & archived" },
        { value: "true", label: "Active" },
        { value: "false", label: "Archived" },
      ],
      showReleaseDialog: false,
      releaseTarget: null,
      releaseReason: "",
      releasingUnit: false,
    };
  },
  computed: {
    orderedTypes() {
      return [...this.types].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    },
    activeTypeOptions() {
      return this.orderedTypes
        .filter((type) => type.active !== false || type.id == this.editingUnit?.operational_unit_type_id)
        .map((type) => ({ value: type.id, label: type.singular_label }));
    },
    typeFilterOptions() {
      return [
        { value: "", label: "All types" },
        ...this.orderedTypes.map((type) => ({ value: type.id, label: type.plural_label })),
      ];
    },
    parentOptions() {
      return [
        { value: "", label: "No parent" },
        ...this.units
          .filter((unit) => unit.id !== this.editingUnit?.id && unit.active !== false)
          .map((unit) => ({ value: unit.id, label: unit.name })),
      ];
    },
    filteredUnits() {
      return this.units.filter((unit) => {
        const typeId = unit.operational_unit_type_id || unit.operational_unit_type?.id;
        if (this.filters.typeId && typeId != this.filters.typeId) return false;
        if (this.filters.active && String(unit.active !== false) !== this.filters.active) return false;
        return !this.filters.availability || this.availabilityFor(unit) === this.filters.availability;
      });
    },
    hierarchicalUnits() {
      const visible = new Map(this.filteredUnits.map((unit) => [String(unit.id), unit]));
      const children = new Map();
      this.filteredUnits.forEach((unit) => {
        const parentKey = visible.has(String(unit.parent_id)) ? String(unit.parent_id) : "root";
        if (!children.has(parentKey)) children.set(parentKey, []);
        children.get(parentKey).push(unit);
      });
      children.forEach((items) =>
        items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)),
      );
      const result = [];
      const walk = (parentKey, depth) => {
        (children.get(parentKey) || []).forEach((unit) => {
          result.push({ unit, depth });
          walk(String(unit.id), depth + 1);
        });
      };
      walk("root", 0);
      return result;
    },
    summaryMetrics() {
      const stats = this.statistics || {};
      const count = (status) =>
        stats[status] ??
        stats[`${status}_count`] ??
        this.availability.filter((entry) => this.statusOf(entry) === status).length;
      return [
        { label: "Configured units", value: stats.total ?? stats.total_units ?? this.units.length },
        { label: "Available", value: count("available") },
        { label: "Occupied", value: count("occupied") },
        { label: "Out of service", value: stats.out_of_service ?? count("unavailable") },
      ];
    },
  },
  watch: {
    storeId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    async load() {
      if (!this.storeId) return;
      this.loading = true;
      this.error = "";
      try {
        const [types, units, availability, statistics] = await Promise.all([
          getOperationalUnitTypes(this.storeId, { include_archived: true }),
          getOperationalUnits(this.storeId, { include_archived: true }),
          getOperationalUnitAvailability(this.storeId),
          getOperationalUnitStatistics(this.storeId).catch(() => ({})),
        ]);
        this.types = types;
        this.units = units;
        this.availability = availability;
        this.statistics = statistics || {};
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to load operational-unit settings.");
      } finally {
        this.loading = false;
      }
    },
    typeFor(unit) {
      const typeId = unit.operational_unit_type_id || unit.operational_unit_type?.id;
      return this.types.find((type) => type.id == typeId);
    },
    availabilityUnitId(entry) {
      return entry?.operational_unit_id || entry?.unit_id || entry?.operational_unit?.id || entry?.unit?.id || entry?.id;
    },
    statusOf(entry) {
      return String(entry?.effective_availability || entry?.availability || entry?.status || "").toLowerCase();
    },
    availabilityFor(unit) {
      if (unit.out_of_service) return "unavailable";
      const entry = this.availability.find((item) => this.availabilityUnitId(item) == unit.id);
      return this.statusOf(entry) || "available";
    },
    availabilityTone(status) {
      return { available: "success", occupied: "warning", reserved: "primary", unavailable: "danger" }[status] || "neutral";
    },
    openTypeForm(type = null) {
      this.editingType = type;
      this.typeForm = type ? { ...blankType(), ...type } : blankType();
      this.showTypeForm = true;
    },
    openUnitForm(unit = null) {
      this.editingUnit = unit;
      this.attributesError = "";
      this.unitForm = unit
        ? {
            ...blankUnit(),
            ...unit,
            operational_unit_type_id:
              unit.operational_unit_type_id || unit.operational_unit_type?.id || "",
            parent_id: unit.parent_id || unit.parent?.id || "",
            attributes: JSON.stringify(unit.attributes || {}, null, 2),
          }
        : { ...blankUnit(), operational_unit_type_id: this.types.find((type) => type.active !== false)?.id || "" };
      this.showUnitForm = true;
    },
    async saveType() {
      if (!this.typeForm.code || !this.typeForm.singular_label || !this.typeForm.plural_label) return;
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          ...this.typeForm,
          code: this.typeForm.code.trim().toUpperCase(),
          singular_label: this.typeForm.singular_label.trim(),
          plural_label: this.typeForm.plural_label.trim(),
          sort_order: Number(this.typeForm.sort_order) || 0,
        };
        if (this.editingType) {
          await updateOperationalUnitType(this.storeId, this.editingType.id, payload);
        } else {
          await createOperationalUnitType(this.storeId, payload);
        }
        this.showTypeForm = false;
        await this.load();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to save the operational-unit type.");
      } finally {
        this.saving = false;
      }
    },
    async saveUnit() {
      if (!this.unitForm.operational_unit_type_id || !this.unitForm.code || !this.unitForm.name) return;
      let attributes;
      try {
        attributes = this.unitForm.attributes.trim() ? JSON.parse(this.unitForm.attributes) : {};
        this.attributesError = "";
      } catch {
        this.attributesError = "Enter valid JSON.";
        return;
      }
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          ...this.unitForm,
          operational_unit_type_id: this.unitForm.operational_unit_type_id,
          code: this.unitForm.code.trim().toUpperCase(),
          name: this.unitForm.name.trim(),
          parent_id: this.unitForm.parent_id || null,
          revenue_center_id: this.unitForm.revenue_center_id || null,
          capacity: this.unitForm.capacity === "" ? null : Number(this.unitForm.capacity),
          sort_order: Number(this.unitForm.sort_order) || 0,
          attributes,
        };
        if (this.editingUnit) {
          await updateOperationalUnit(this.storeId, this.editingUnit.id, payload);
        } else {
          await createOperationalUnit(this.storeId, payload);
        }
        this.showUnitForm = false;
        await this.load();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to save the operational unit.");
      } finally {
        this.saving = false;
      }
    },
    async toggleTypeArchive(type) {
      await this.patchType(type, { active: type.active === false });
    },
    async patchType(type, payload) {
      try {
        await updateOperationalUnitType(this.storeId, type.id, payload);
        await this.load();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to update the type.");
      }
    },
    async toggleUnitArchive(unit) {
      await this.patchUnit(unit, { active: unit.active === false });
    },
    async toggleOutOfService(unit) {
      await this.patchUnit(unit, { out_of_service: !unit.out_of_service });
    },
    async patchUnit(unit, payload) {
      try {
        await updateOperationalUnit(this.storeId, unit.id, payload);
        await this.load();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to update the unit.");
      }
    },
    openReleaseDialog(unit) {
      this.releaseTarget = unit;
      this.releaseReason = "";
      this.showReleaseDialog = true;
    },
    async releaseUnit() {
      if (!this.releaseReason.trim()) {
        this.error = "Please provide a reason for releasing the unit.";
        return;
      }
      this.releasingUnit = true;
      this.error = "";
      try {
        await releaseOperationalUnit(this.storeId, this.releaseTarget.id, {
          reason: this.releaseReason,
        });
        this.showReleaseDialog = false;
        this.releaseTarget = null;
        this.releaseReason = "";
        await this.load();
      } catch (error) {
        this.error = operationalUnitError(error, "Unable to release the unit.");
      } finally {
        this.releasingUnit = false;
      }
    },
  },
};
</script>

<style scoped>
.ou-settings { display: grid; gap: 1rem; }
.ou-settings__hero, .ou-settings__section { background: var(--hs-surface, #fff); border: 1px solid var(--hs-border, #e2e8f0); border-radius: 20px; padding: 1.1rem; }
.ou-settings__hero { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; }
.ou-settings__hero h2 { color: var(--hs-text, #0f172a); font-size: 1.35rem; font-weight: 950; margin: 0; }
.ou-settings__hero p, .ou-section-title p { color: var(--hs-muted, #64748b); font-size: .84rem; line-height: 1.5; margin: .35rem 0 0; max-width: 720px; }
.ou-settings__eyebrow { color: var(--hs-primary, #3293ec) !important; font-size: .7rem !important; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; }
.ou-settings__actions, .ou-row-actions, .ou-unit__badges { align-items: center; display: flex; flex-wrap: wrap; gap: .5rem; }
.ou-stat-grid { display: grid; gap: .75rem; grid-template-columns: repeat(4, 1fr); }
.ou-stat-grid article { background: var(--hs-surface, #fff); border: 1px solid var(--hs-border, #e2e8f0); border-radius: 16px; display: grid; gap: .2rem; padding: .9rem; }
.ou-stat-grid span { color: var(--hs-muted, #64748b); font-size: .74rem; font-weight: 800; }
.ou-stat-grid strong { color: var(--hs-text, #0f172a); font-size: 1.4rem; }
.ou-section-title { align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 1rem; }
.ou-section-title h3 { color: var(--hs-text, #0f172a); font-size: 1rem; font-weight: 950; margin: 0; }
.ou-types { display: grid; gap: .6rem; grid-template-columns: repeat(auto-fill, minmax(290px, 1fr)); }
.ou-type, .ou-unit { align-items: center; background: var(--hs-bg, #f8fafc); border: 1px solid var(--hs-border, #e2e8f0); border-radius: 14px; display: flex; gap: .75rem; padding: .8rem; }
.ou-type--archived, .ou-unit--archived { opacity: .68; }
.ou-type__swatch, .ou-unit__icon { align-items: center; border-radius: 10px; color: white; display: inline-flex; flex: 0 0 36px; height: 36px; justify-content: center; }
.ou-type > div:nth-child(2), .ou-unit__identity > div { display: grid; flex: 1; }
.ou-type strong, .ou-unit strong { color: var(--hs-text, #0f172a); font-size: .87rem; }
.ou-type small, .ou-unit small, .ou-unit__badges > span { color: var(--hs-muted, #64748b); font-size: .72rem; }
.ou-row-actions { justify-content: flex-end; margin-left: auto; }
.ou-row-actions button { color: var(--hs-primary, #3293ec); font-size: .72rem; font-weight: 850; }
.ou-row-actions button.danger { color: var(--hs-danger, #b91c1c); }
.ou-filters { display: grid; gap: .75rem; grid-template-columns: repeat(3, minmax(150px, 240px)); margin-bottom: 1rem; }
.ou-unit-list { display: grid; gap: .55rem; }
.ou-unit { margin-left: calc(var(--depth) * 1.25rem); }
.ou-unit__identity { align-items: center; display: flex; flex: 1; gap: .7rem; min-width: 210px; }
.ou-unit__branch { color: var(--hs-muted, #64748b); display: none; }
.ou-unit[style*="--depth: 1"] .ou-unit__branch, .ou-unit[style*="--depth: 2"] .ou-unit__branch { display: inline; }
.ou-empty { align-items: center; color: var(--hs-muted, #64748b); display: flex; flex-direction: column; font-size: .82rem; gap: .25rem; justify-content: center; min-height: 100px; text-align: center; }
.ou-form { display: grid; gap: .9rem; grid-template-columns: 1fr 1fr; }
.ou-form__wide { grid-column: 1 / -1; }
.ou-form__info { color: var(--hs-text, #0f172a); font-size: .87rem; grid-column: 1 / -1; margin: 0; padding: .5rem; background: var(--hs-bg, #f8fafc); border-radius: 8px; }
.ou-native-field { display: grid; gap: .42rem; }
.ou-native-field span { color: var(--hs-text, #0f172a); font-size: .82rem; font-weight: 950; }
.ou-native-field input { height: 2.75rem; width: 100%; }
@media (max-width: 900px) {
  .ou-settings__hero { display: grid; }
  .ou-stat-grid { grid-template-columns: repeat(2, 1fr); }
  .ou-unit { align-items: flex-start; flex-direction: column; margin-left: 0; }
  .ou-row-actions { justify-content: flex-start; margin-left: 0; }
}
@media (max-width: 640px) {
  .ou-form, .ou-filters { grid-template-columns: 1fr; }
  .ou-form__wide { grid-column: auto; }
}
</style>
